// Sistem yazıcı röle sistemi:
// - Direkt yazdırma (local agent / Electron) aktif olan cihaz: host olarak heartbeat + iş kuyruğu poll.
// - Her cihaz: yazdırma denerken yerel ortam yazıcı yoksa raw HTML'i backend'e gönderir;
//   backend mağazadaki bir host'a atar; o cihaz handlePrint() ile basar.

import { API_URL } from '../config/api';
import { handlePrint } from './printHelper';

const DEVICE_ID_KEY = 'akikDeviceId';

export const getDeviceId = () => {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
};

const getDeviceName = () => {
  const ua = navigator.userAgent || '';
  if (/iPad/.test(ua)) return 'iPad';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Tarayıcı';
};

// HTML <-> base64 (UTF-8 güvenli)
const htmlToB64 = (html) => {
  const utf8 = unescape(encodeURIComponent(html));
  return btoa(utf8);
};
const b64ToHtml = (b64) => {
  const bin = atob(b64);
  return decodeURIComponent(escape(bin));
};

// Cihaz sistem yazıcı host'u olabilir mi?
// Direkt yazdırma aktif + fiş yazıcısı seçili VEYA tamirat yazıcı sunucusu + tamirat yazıcısı seçili olmalı.
const canActAsSystemPrintHost = () => {
  const directEnabled = localStorage.getItem('directPrintEnabled') === 'true';
  if (!directEnabled) return false;
  const printerName = localStorage.getItem('receiptPrinter');
  const isRepairHost = localStorage.getItem('isRepairPrintHost') === 'true';
  const repairPrinter = localStorage.getItem('repairPrinter');
  return !!printerName || (isRepairHost && !!repairPrinter);
};

// HOST tarafı: heartbeat
let heartbeatTimer = null;
let heartbeatVisibilityHandler = null;
export const startSystemPrintHostHeartbeat = (storeId) => {
  stopSystemPrintHostHeartbeat();
  if (!storeId) return;
  const send = async () => {
    try {
      if (!canActAsSystemPrintHost()) return;
      const printerName = localStorage.getItem('receiptPrinter');
      const isRepairHost = localStorage.getItem('isRepairPrintHost') === 'true';
      const repairPrinterName = localStorage.getItem('repairPrinter');
      await fetch(`${API_URL}/system-print-devices/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_id: getDeviceId(),
          store_id: storeId,
          device_name: getDeviceName(),
          printer_name: printerName,
          is_repair_host: isRepairHost ? 1 : 0,
          repair_printer_name: repairPrinterName || null,
        }),
      });
    } catch (e) { /* sessizce yut */ }
  };
  send();
  heartbeatTimer = setInterval(send, 15000);
  // Sekme/pencere arka plandayken tarayıcı setInterval'ı yavaşlatıyor (throttling) - host
  // bilgisayarın ekranı bir süre arka planda kalırsa heartbeat gecikip "stale" görünebiliyor,
  // bu da o sırada oluşan fişlerin geç (ekrana geri dönülünce) basılmasına yol açıyordu.
  // Sekme tekrar görünür/aktif olduğu an hemen bir heartbeat atarak bunu telafi ediyoruz.
  heartbeatVisibilityHandler = () => { if (document.visibilityState === 'visible') send(); };
  document.addEventListener('visibilitychange', heartbeatVisibilityHandler);
  window.addEventListener('focus', heartbeatVisibilityHandler);
};

export const stopSystemPrintHostHeartbeat = () => {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  if (heartbeatVisibilityHandler) {
    document.removeEventListener('visibilitychange', heartbeatVisibilityHandler);
    window.removeEventListener('focus', heartbeatVisibilityHandler);
    heartbeatVisibilityHandler = null;
  }
  try {
    fetch(`${API_URL}/system-print-devices/heartbeat?device_id=${encodeURIComponent(getDeviceId())}`, {
      method: 'DELETE',
      keepalive: true,
    });
  } catch (e) { /* yut */ }
};

// HOST tarafı: pending job polling. Aldığı HTML'i handlePrint ile basar.
let pollTimer = null;
let pollInFlight = false;
let pollVisibilityHandler = null;
export const startSystemPrintHostJobPolling = () => {
  stopSystemPrintHostJobPolling();
  const tick = async () => {
    // Önceki tur (örn. yavaş bir yazdırma) hâlâ sürüyorsa yeni turu atla —
    // aksi halde aynı iş henüz backend'de tamamlanmadan tekrar çekilip iki kez basılabilir.
    if (pollInFlight) return;
    pollInFlight = true;
    try {
      if (!canActAsSystemPrintHost()) return;
      const res = await fetch(`${API_URL}/system-print-jobs/pending?device_id=${encodeURIComponent(getDeviceId())}`);
      if (!res.ok) return;
      const data = await res.json();
      if (!data.success || !Array.isArray(data.jobs) || data.jobs.length === 0) return;
      for (const job of data.jobs) {
        try {
          const html = b64ToHtml(job.payload_b64);
          const printType = job.print_role === 'repair' ? 'repair' : 'receipt';
          await handlePrint(html, printType);
          await fetch(`${API_URL}/print-jobs/${job.id}/complete`, { method: 'POST' });
        } catch (err) {
          await fetch(`${API_URL}/print-jobs/${job.id}/fail`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ error_message: err?.message || 'Sistem yazdırma hatası' }),
          });
        }
      }
    } catch (e) { /* yut */ }
    finally {
      pollInFlight = false;
    }
  };
  pollTimer = setInterval(tick, 2500);
  tick();
  // Aynı throttling sorunu iş kuyruğu için de geçerli - sekme arka plandan dönünce
  // bekleyen bir fiş varsa 2.5sn'lik bir sonraki tur yerine hemen çekilsin.
  pollVisibilityHandler = () => { if (document.visibilityState === 'visible') tick(); };
  document.addEventListener('visibilitychange', pollVisibilityHandler);
  window.addEventListener('focus', pollVisibilityHandler);
};

export const stopSystemPrintHostJobPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (pollVisibilityHandler) {
    document.removeEventListener('visibilitychange', pollVisibilityHandler);
    window.removeEventListener('focus', pollVisibilityHandler);
    pollVisibilityHandler = null;
  }
};

// KAYNAK tarafı: HTML yazdırma — önce yerel direkt yazdırma, başarısızsa röle.
// role: 'receipt' (fiş yazıcısı) | 'repair' (tamirat yazıcısı)
// returns: { mode: 'local'|'relay', targetName?: string, pending?: bool }
export const printSystemReceipt = async (html, storeId, role = 'receipt') => {
  const printType = role === 'repair' ? 'repair' : 'receipt';

  // 1) Yerel direkt yazdırma denemesi — bu cihazda ilgili yazıcı varsa.
  //    Tamirat fişi yalnızca bu cihaz "Tamirat Yazıcı Sunucusu" ise ve tamirat yazıcısı seçiliyse yerelde basılır;
  //    aksi halde röleye düşer ki backend tamirat host'unu bulabilsin.
  if (canActAsSystemPrintHost()) {
    const isRepairHost = localStorage.getItem('isRepairPrintHost') === 'true';
    const canPrintLocally = printType === 'repair'
      ? (isRepairHost && !!localStorage.getItem('repairPrinter'))
      : !!localStorage.getItem('receiptPrinter');
    if (canPrintLocally) {
      try {
        const ok = await handlePrint(html, printType);
        if (ok) return { mode: 'local' };
      } catch (e) {
        console.warn('Yerel sistem yazdırma başarısız, röleye düşülüyor:', e?.message);
      }
    }
  }

  // 2) Röle: backend'e job at
  if (!storeId) {
    throw new Error('Mağaza bilgisi yok, sistem yazdırma yapılamadı');
  }

  const payload_b64 = htmlToB64(html);
  const res = await fetch(`${API_URL}/system-print-jobs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      store_id: storeId,
      source_device_id: getDeviceId(),
      payload_b64,
      kind: printType,
    }),
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data?.message || 'Mağazada açık bir yazıcılı bilgisayar bulunamadı');
  }

  const jobId = data.job_id;
  const targetName = data.target_device_name || 'cihaz';
  const start = Date.now();
  while (Date.now() - start < 30000) {
    await new Promise(r => setTimeout(r, 2000));
    try {
      const sres = await fetch(`${API_URL}/print-jobs/${jobId}/status`);
      const sd = await sres.json();
      if (sd?.success && sd.job) {
        if (sd.job.status === 'completed') return { mode: 'relay', targetName };
        if (sd.job.status === 'failed') throw new Error(sd.job.error_message || 'Uzak cihaz yazdıramadı');
      }
    } catch (e) {
      if (e?.message && e.message !== 'Failed to fetch') throw e;
    }
  }
  return { mode: 'relay', targetName, pending: true };
};
